/**
 * Checkbox row for toggling individual keywords with an inline score input.
 *
 * @module components/sim/keywords/KeywordsCheckbox
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import { Checkbox, Input, NativeSelect } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import PropTypes from 'prop-types';
import FormHelperText from '@material-ui/core/FormHelperText';
import Tooltip from '@material-ui/core/Tooltip';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useCheckboxStyles, useStylesBootstrap } from './KeywordsCheckbox.js';

/**
 * KeywordsCheckbox is a React functional component responsible for rendering a form control
 * with a customizable checkbox and input field. This component supports different field types
 * (e.g., dropdown or text input) and allows users to interact with the respective controls.
 *
 * @param {Object} props - The props object for configuring the component.
 * @param {string} props.name - The name of the field, used for display and identification.
 * @param {string|number} props.value - The current value of the field.
 * @param {Function} props.handleCheckboxChange - Callback function triggered when the checkbox is toggled.
 * @param {Function} props.handleInputChange - Callback function triggered when the input value changes.
 * @param {number} props.fieldTitleIndex - Index of the field within the title context.
 * @param {number} props.fieldIndex - Index of the field within its container.
 * @param {boolean} props.scorable - Indicates whether the field is scorable.
 * @param {Function} props.save - Callback function triggered when the input field loses focus.
 * @param {string} props.fieldType - The type of field, defining its behavior (e.g., "picklist" or "text").
 * @param {Array<string>} props.options - List of selectable options for a dropdown field.
 * @returns {JSX.Element} The configured field component.
 */
const KeywordsCheckbox = ({
    name,
    value,
    handleCheckboxChange,
    handleInputChange,
    fieldTitleIndex,
    fieldIndex,
    scorable,
    save,
    fieldType,
    options,
}) => {
    const classes = useCheckboxStyles();

    /**
     * A functional component that renders a customized Bootstrap-styled Tooltip.
     *
     * @param {Object} props - The properties to be passed to the Tooltip component.
     * @return {JSX.Element} A Tooltip component with applied Bootstrap-related styles and given props.
     */
    function BootstrapTooltip(props) {
        const classes = useStylesBootstrap();

        return <Tooltip arrow classes={classes} {...props} />;
    }

    return (
        <FormControl className={classes.formControl}>
            <FormHelperText>{name}</FormHelperText>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                { fieldType.toLowerCase() === 'picklist'
                    ? (
                        <NativeSelect
                            value={value}
                            onChange={(e) => handleInputChange(e, fieldTitleIndex, fieldIndex)}
                            className={classes.select}
                            fullWidth
                            required
                            endAdornment={(
                                <InputAdornment position="start">
                                    <BootstrapTooltip
                                        placement="top"
                                        title="Please check this box for each answers if they are scorable"
                                    >
                                        <Checkbox
                                            name={name}
                                            value={name}
                                            color="primary"
                                            className={classes.checkbox}
                                            checked={scorable}
                                            onChange={(e) => handleCheckboxChange(e, fieldTitleIndex, fieldIndex)}
                                        />
                                    </BootstrapTooltip>
                                </InputAdornment>
                            )}
                        >
                            { options && options.map((option) => (
                                <option value={option} key={option}>
                                    {option}
                                </option>
                            ))}
                        </NativeSelect>
                    )
                    : (
                        <Input
                            fullWidth
                            value={value}
                            className={classes.textFont}
                            onBlur={save}
                            onChange={(e) => handleInputChange(e, fieldTitleIndex, fieldIndex)}
                            endAdornment={(
                                <InputAdornment position="start">
                                    <BootstrapTooltip
                                        placement="top"
                                        title="Please check this box for each answers if they are scorable"
                                    >
                                        <Checkbox
                                            name={name}
                                            value={name}
                                            color="primary"
                                            className={classes.checkbox}
                                            checked={scorable}
                                            onChange={(e) => handleCheckboxChange(e, fieldTitleIndex, fieldIndex)}
                                        />
                                    </BootstrapTooltip>
                                </InputAdornment>
                            )}
                        />
                    )}
            </div>
        </FormControl>
    );
};

/**
 * Prop types definition for the KeywordsCheckbox component.
 *
 * This component is used to render a checkbox and manage its state
 * based on provided keyword data. The prop types below define
 * the expected shape and types of the props passed to the component.
 *
 * @property {boolean} isChecked - Indicates whether the checkbox is checked.
 * @property {string} label - The label text displayed alongside the checkbox.
 * @property {function} onChange - Callback function triggered when the checkbox state changes.
 */
KeywordsCheckbox.propTypes = {
    name: PropTypes.string,
    value: PropTypes.string,
    handleCheckboxChange: PropTypes.func,
    handleInputChange: PropTypes.func,
    fieldTitleIndex: PropTypes.number,
    fieldIndex: PropTypes.number,
    scorable: PropTypes.bool,
    save: PropTypes.func,
    fieldType: PropTypes.string,
    options: PropTypes.array,
};

export default KeywordsCheckbox;
